import {
  FolderTreeItem,
  useFolderActionPermissions,
  useSupportedAssets,
} from "@zapier/client";
import { ShareModal, useEmitShareInteractionEvent } from "@zapier/shared";
import { useEffect } from "react";
import { NonMpeFolderSharingModal } from "./NonMpeFolderSharingModal";

interface FolderSharingModalProps {
  folder?: FolderTreeItem;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function FolderSharingModal({
  folder,
  isOpen,
  setIsOpen,
}: FolderSharingModalProps) {
  const { emit: emitShareEvent } = useEmitShareInteractionEvent();
  const { isMPFEnabled } = useSupportedAssets();
  const { canShare, enablePermissionsCheck } = useFolderActionPermissions({
    folderId: folder?.id ?? "",
  });

  // Check permissions as needed
  useEffect(() => {
    if (isMPFEnabled && isOpen) {
      enablePermissionsCheck();
    }
  }, [isOpen, enablePermissionsCheck, isMPFEnabled]);

  if (!isMPFEnabled) {
    return (
      <NonMpeFolderSharingModal
        folderId={folder?.id}
        folder={folder}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    );
  }

  return (
    <ShareModal
      allowSharingWithEveryone={true}
      allowSharingWithTeams={true}
      allowSharingWithAdmins={isMPFEnabled}
      assetId={folder?.id ?? ""}
      assetName={folder?.name || "Untitled folder"}
      assetOwner={folder?.owner ?? undefined}
      assetType="folder"
      // Setting to false until we're ready to add owner changing
      canChangeOwner={false}
      canShare={canShare}
      emit={(subjectId: string) => {
        emitShareEvent({
          assetId: folder?.id,
          optionSelected: "share_folder",
          subjectId,
        });
      }}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      roles={["editor"]}
    />
  );
}
